// Chain-lightning target ordering (pure, Phaser-free; tests/combat-chain.test.ts).

export interface ChainNode {
  x: number;
  y: number;
  /** Approximate body radius; distances are measured to the edge. */
  radius: number;
}

/**
 * Strike order for chain lightning.
 * The first target is `first` (an index into `nodes`) when given (>= 0), otherwise the candidate
 * nearest to `from` whose edge lies within `range`. Then, `jumps` times, the lightning hops to the
 * nearest not-yet-struck candidate within `range` of the previous target. Ties keep list order.
 * `skip[i] === true` excludes a candidate entirely.
 */
export function chainOrder(
  from: { x: number; y: number },
  nodes: readonly ChainNode[],
  first: number,
  jumps: number,
  range: number,
  skip?: readonly boolean[],
): number[] {
  const n = nodes.length;
  const used = new Array<boolean>(n).fill(false);
  if (skip) for (let i = 0; i < n; i++) if (skip[i]) used[i] = true;
  const order: number[] = [];
  let cx = from.x;
  let cy = from.y;

  let start = -1;
  if (first >= 0 && first < n && !used[first]) start = first;
  else if (first < 0) start = nearest(cx, cy, nodes, used, range);
  if (start < 0) return order;
  order.push(start);
  used[start] = true;
  cx = nodes[start].x;
  cy = nodes[start].y;

  const hops = Math.max(0, Math.floor(jumps));
  for (let j = 0; j < hops; j++) {
    const next = nearest(cx, cy, nodes, used, range);
    if (next < 0) break;
    order.push(next);
    used[next] = true;
    cx = nodes[next].x;
    cy = nodes[next].y;
  }
  return order;
}

function nearest(x: number, y: number, nodes: readonly ChainNode[], used: readonly boolean[], range: number): number {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < nodes.length; i++) {
    if (used[i]) continue;
    const c = nodes[i];
    const d = Math.max(0, Math.hypot(c.x - x, c.y - y) - Math.max(0, c.radius));
    if (d <= range && d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}
